// VideoStyle.js
import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Card, Icon, Alert, Modal, Button } from 'antd'
import AspectRatioSelector from './components/AspectRatioSelector'
import CinematicIcon from '../../../../assets/video-cinematic.png'
import AnimeIcon from '../../../../assets/video-anime.png'
import ComicBookIcon from '../../../../assets/video-comic.png'
import OrigamiIcon from '../../../../assets/video-origami.png'
import DisneyIcon from '../../../../assets/video-disney.png'
import RealisticIcon from '../../../../assets/video-realistic.png'

import './VideoStyle.scss'
import StepTitle from '../StepTitle'

const { Meta } = Card

const videoStyles = [
  {
    key: 'realistic',
    title: 'Realistic',
    description: 'Photorealistic style with natural lighting and textures',
    icon: RealisticIcon
  },
  {
    key: 'cinematic black & white',
    title: 'Cinematic',
    description: 'Photorealistic style with natural lighting and textures',
    icon: CinematicIcon
  },
  {
    key: 'comic book',
    title: 'Comic Book',
    description: 'Bold colors, strong outlines, and dramatic shading',
    icon: ComicBookIcon
  },
  {
    key: 'anime',
    title: 'Anime',
    description: 'Japanese animation style with vibrant colors',
    icon: AnimeIcon
  },
  {
    key: 'origami',
    title: 'Origami',
    description: 'Futuristic cyberpunk aesthetic with neon elements',
    icon: OrigamiIcon
  },
  {
    key: 'disney cartoon',
    title: 'Cartoon',
    description: 'Classic Disney characters with vibrant colors',
    icon: DisneyIcon
  }
]

// Lazy Image Component with fallback for older browsers
const LazyImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const imgRef = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const handleLoad = () => {
    setIsLoaded(true)
  }

  return (
    <div ref={imgRef} className={className}>
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={handleLoad}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      )}
      {!isLoaded && isInView && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#f5f5f5',
            color: '#ccc'
          }}
        >
          Loading...
        </div>
      )}
    </div>
  )
}

const VideoStyle = ({
  setFormData,
  formData,
  onValidationChange,
  onStyleChange
}) => {
  const [selectedStyle, setSelectedStyle] = useState(null)
  const [showStyleChangeModal, setShowStyleChangeModal] = useState(false)
  const [pendingStyleKey, setPendingStyleKey] = useState(null)

  const isValid = useMemo(() => {
    return (
      Boolean(formData.aspectRatio) &&
      Boolean(formData.videoStyle && formData.videoStyle.key)
    )
  }, [formData.aspectRatio, formData.videoStyle])

  useEffect(() => {
    onValidationChange(prev => ({ ...prev, videoStyle: isValid }))
  }, [isValid])

  // Sync internal state with formData changes (e.g. after cloning or loading new video)
  useEffect(() => {
    if (formData.videoStyle && formData.videoStyle.key) {
      // Check if currently selected style differs from the one in formData
      if (selectedStyle !== formData.videoStyle.key) {
        setSelectedStyle(null) // Reset local override so it falls back to formData
      }
    }
  }, [formData.videoStyle])

  const hasGeneratedContent =
    formData.shotList &&
    formData.shotList.length > 0 &&
    formData.shotList.some(scene => scene.shots.some(shot => shot.imageSrc))

  const handleStyleSelect = key => {
    const selectedStyleObj = videoStyles.find(style => style.key === key)
    const currentStyleKey = formData.videoStyle && formData.videoStyle.key

    // Check if style is actually changing and if we have generated content
    if (
      currentStyleKey &&
      currentStyleKey !== key &&
      hasGeneratedContent &&
      onStyleChange
    ) {
      // Style is changing and we have content, show modal
      setPendingStyleKey(key)
      setShowStyleChangeModal(true)
    } else {
      // No content yet or same style, just update directly
      setSelectedStyle(key)
      setFormData(prev => ({
        ...prev,
        videoStyle: selectedStyleObj
      }))
    }
  }

  const handleClone = () => {
    Modal.confirm({
      title: 'Confirm Clone',
      content: 'Are you sure you want to create a new copy of this video?',
      centered: true,
      onOk: () => {
        const selectedStyleObj = videoStyles.find(
          style => style.key === pendingStyleKey
        )
        if (onStyleChange && selectedStyleObj) {
          onStyleChange(selectedStyleObj, true) // true = clone
        }
        setShowStyleChangeModal(false)
        setPendingStyleKey(null)
      }
    })
  }

  const handleOverride = () => {
    Modal.confirm({
      title: 'Confirm Override',
      content:
        'This will regenerate all images. Are you sure you want to continue?',
      centered: true,
      okType: 'danger',
      onOk: () => {
        const selectedStyleObj = videoStyles.find(
          style => style.key === pendingStyleKey
        )
        if (onStyleChange && selectedStyleObj) {
          onStyleChange(selectedStyleObj, false) // false = override
        } else if (selectedStyleObj) {
          // Fallback to direct update if handler not provided
          setSelectedStyle(pendingStyleKey)
          setFormData(prev => ({
            ...prev,
            videoStyle: selectedStyleObj
          }))
        }
        setShowStyleChangeModal(false)
        setPendingStyleKey(null)
      }
    })
  }

  const handleCancelStyleChange = () => {
    setShowStyleChangeModal(false)
    setPendingStyleKey(null)
  }

  const getPendingStyleName = () => {
    const style = videoStyles.find(s => s.key === pendingStyleKey)
    return style ? style.title : pendingStyleKey
  }

  const handleRatioChange = ratio => {
    setFormData(prev => ({
      ...prev,
      aspectRatio: ratio
    }))
  }

  return (
    <div className="visual-setup-container">
      <StepTitle
        iconType="camera"
        title="Visual Setup"
        description="Define your video's canvas format and artistic direction."
      />

      <div className="setup-sections-wrapper">
        <div className="setup-sections">
          {/* Section 01: Aspect Ratio */}
          <div className="setup-section">
            <div className="section-header-simple">
              <span className="number-dot">1</span>
              <h3>Canvas aspect ratio</h3>
            </div>
            <AspectRatioSelector
              value={formData.aspectRatio || '16:9'}
              onChange={handleRatioChange}
            />
          </div>

          {/* Section 02: Artistic Style */}
          <div className="setup-section style-section">
            <div className="section-header-simple">
              <span className="number-dot">2</span>
              <h3>Video artistic style</h3>
            </div>

            {!formData.videoStyle?.key && (
              <div className="selection-reminder">
                <Icon type="info-circle" />
                <span>Please select a style to proceed</span>
              </div>
            )}

            <div className="shot-list-cards">
              {videoStyles.map(style => (
                <Card
                  key={style.key}
                  className={`shot-list-card ${
                    selectedStyle === style.key ||
                    (formData.videoStyle &&
                      formData.videoStyle.key === style.key)
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() => handleStyleSelect(style.key)}
                  cover={
                    <div
                      className="shot-list-card-image"
                      style={{ backgroundColor: '#f8fafc' }}
                    >
                      <LazyImage src={style.icon} alt={style.title} />
                    </div>
                  }
                >
                  <Meta title={style.title} description={style.description} />
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Style Change Confirmation Modal */}
      <Modal
        visible={showStyleChangeModal}
        onCancel={handleCancelStyleChange}
        footer={null}
        centered
        width={520}
        className="video-style-change-modal"
      >
        <div className="video-style-change-modal-content">
          <div className="modal-icon-wrapper">
            <Icon type="warning" className="modal-warning-icon" />
          </div>
          <h3 className="modal-title">Existing Generated Content Detected</h3>
          <p className="modal-description">
            You have already generated images for this video. Changing the style
            to <strong>"{getPendingStyleName()}"</strong> conflicts with your
            existing work.
            <br />
            <br />
            Please choose how you would like to proceed:
          </p>
          <div className="modal-options">
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="copy" />
              </div>
              <div className="option-content">
                <h4 className="option-title">
                  Clone as New Video (Recommended)
                </h4>
                <p className="option-description">
                  Safely creates a brand new copy of this video with the new
                  style. Your current video and its images will be saved and
                  remain untouched.
                </p>
              </div>
            </div>
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="edit" />
              </div>
              <div className="option-content">
                <h4 className="option-title">Override Current Video</h4>
                <p className="option-description">
                  Updates this exact video.
                  <p
                    style={{
                      border: '1px solid red',
                      borderRadius: '5px',
                      padding: '5px 10px',
                      marginTop: '10px',
                      backgroundColor: '#fff8f8ff'
                    }}
                  >
                    <strong>
                      <Icon type="warning" /> Warning:
                    </strong>{' '}
                    All your existing images will be deleted and regenerated to
                    match the new style. This cannot be undone.
                  </p>
                </p>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <Button onClick={handleCancelStyleChange} size="large">
              Cancel
            </Button>

            <div className="modal-actions-right">
              <Button
                type="default"
                onClick={handleOverride}
                size="large"
                icon="edit"
              >
                Override
              </Button>
              <Button
                type="primary"
                onClick={handleClone}
                size="large"
                icon="copy"
              >
                Clone
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default VideoStyle
